/** Row-major cell indices `a` and `b` share an edge on a `gridSize`×`gridSize` board. */
export function gridCellsAdjacent(gridSize, indexA, indexB) {
    if (indexA < 0 || indexB < 0) return false;
    const ra = Math.floor(indexA / gridSize);
    const ca = indexA % gridSize;
    const rb = Math.floor(indexB / gridSize);
    const cb = indexB % gridSize;
    return Math.abs(ra - rb) + Math.abs(ca - cb) === 1;
}

/** Solved layout: slice index `i` sits in visual cell `i`. */
export function solvedLayout(gridSize) {
    const n = gridSize * gridSize;
    return Array.from({ length: n }, (_, i) => i);
}

/**
 * Randomize by applying valid moves from the solved state (always solvable).
 * @param {number} gridSize
 * @param {number} [moveCount]
 */
export function shuffleMoveCount(difficulty) {
    if (difficulty === "easy") return 40;
    if (difficulty === "hard") return 120;
    return 80;
}

export function shuffledLayout(gridSize, moveCount = 80) {
    const n = gridSize * gridSize;
    const emptySlice = n - 1;
    let layout = solvedLayout(gridSize);
    let emptyVisual = layout.findIndex((v) => v === emptySlice);

    for (let m = 0; m < moveCount; m += 1) {
        const neighbors = [];
        for (let cell = 0; cell < n; cell += 1) {
            if (cell !== emptyVisual && gridCellsAdjacent(gridSize, cell, emptyVisual)) {
                neighbors.push(cell);
            }
        }
        if (neighbors.length === 0) break;
        const pick = neighbors[Math.floor(Math.random() * neighbors.length)];
        const next = [...layout];
        [next[pick], next[emptyVisual]] = [next[emptyVisual], next[pick]];
        layout = next;
        emptyVisual = pick;
    }

    if (layout.every((v, i) => v === i)) {
        return shuffledLayout(gridSize, Math.max(20, Math.floor(moveCount / 2)));
    }
    return layout;
}

/**
 * Scramble from solved with a known reverse path — for 1080p auto-solve recording.
 * @param {number} gridSize
 * @param {number} moveCount
 * @param {() => number} [rng] returns [0,1)
 * @returns {{ layout: number[], solveLayouts: number[][] }}
 */
export function scrambleWithSolvePath(gridSize, moveCount = 32, rng = Math.random) {
    const n = gridSize * gridSize;
    const emptySlice = n - 1;
    let layout = solvedLayout(gridSize);
    let emptyVisual = layout.findIndex((v) => v === emptySlice);
    /** Empty visual index before each scramble step (for undo avoidance). */
    let previousEmptyVisual = -1;
    /** Layouts after each scramble step (excludes solved start). */
    const forward = [];

    for (let m = 0; m < moveCount; m += 1) {
        const neighbors = [];
        for (let cell = 0; cell < n; cell += 1) {
            if (cell !== emptyVisual && gridCellsAdjacent(gridSize, cell, emptyVisual)) {
                neighbors.push(cell);
            }
        }
        if (neighbors.length === 0) break;
        let candidates = neighbors;
        if (previousEmptyVisual >= 0 && neighbors.length > 1) {
            const withoutUndo = neighbors.filter((cell) => cell !== previousEmptyVisual);
            if (withoutUndo.length) candidates = withoutUndo;
        }
        const pick = candidates[Math.floor(rng() * candidates.length)];
        const next = [...layout];
        [next[pick], next[emptyVisual]] = [next[emptyVisual], next[pick]];
        previousEmptyVisual = emptyVisual;
        layout = next;
        emptyVisual = pick;
        forward.push(layout.map((v) => Number(v)));
    }

    if (layout.every((v, i) => v === i)) {
        return scrambleWithSolvePath(gridSize, Math.max(8, Math.floor(moveCount / 2)), rng);
    }

    // Walk back through scramble layouts to solved (drop the starting scramble board).
    const solveLayouts = [...forward].reverse().slice(1);
    solveLayouts.push(solvedLayout(gridSize));
    return { layout, solveLayouts };
}
